import { useCallback, useEffect, useRef, useState } from 'react'
import type { Channel, Vod } from '../types'
import { ApiError, fetchChannel, fetchVods } from '../api/kick'
import { kickCache } from '../lib/cache'

type AsyncState<T> = {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

function useAbortable() {
  const controller = useRef<AbortController | null>(null)
  useEffect(() => () => controller.current?.abort(), [])
  const next = useCallback(() => {
    controller.current?.abort()
    const c = new AbortController()
    controller.current = c
    return c
  }, [])
  return next
}

/**
 * Fetches a cached resource and keeps it tied to its key. Data is only exposed
 * while it belongs to the current key, so switching tabs never shows the
 * previous key's data; warm cache hits are read during render to avoid a
 * skeleton flash when the value is already available.
 */
function useCachedResource<T>(
  key: string | null,
  ttlMs: number,
  fetcher: (key: string, signal: AbortSignal) => Promise<T>,
  errorMessage: string,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [dataKey, setDataKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)
  const begin = useAbortable()

  // Keep the latest fetcher without making it an effect dependency.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    if (!key) {
      setData(null)
      setDataKey(null)
      setError(null)
      return
    }
    const controller = begin()
    setLoading(true)
    setError(null)

    kickCache
      .fetch(key, ttlMs, (signal) => fetcherRef.current(key, signal))
      .then((value) => {
        if (controller.signal.aborted) return
        setData(value)
        setDataKey(key)
        setError(null)
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        setData(null)
        setDataKey(null)
        setError(err instanceof ApiError ? err.message : errorMessage)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
  }, [key, nonce, ttlMs, errorMessage, begin])

  const current = dataKey === key ? data : key ? kickCache.get<T>(key) ?? null : null

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data: current, loading, error, reload }
}

export function useChannel(slug: string | null): AsyncState<Channel> {
  return useCachedResource(
    slug ? `channel:${slug}` : null,
    60_000,
    (_key, signal) => fetchChannel(slug as string, signal),
    'Could not load channel',
  )
}

/**
 * Channel metadata for every open tab, so the strip can show avatars and
 * live dots without refetching on each switch.
 */
export function useChannelsMeta(slugs: string[]): Map<string, Channel> {
  const [map, setMap] = useState<Map<string, Channel>>(new Map())
  const key = slugs.join(',')

  useEffect(() => {
    const list = key ? key.split(',') : []
    let cancelled = false

    for (const slug of list) {
      kickCache
        .fetch(`channel:${slug}`, 60_000, (signal) => fetchChannel(slug, signal))
        .then((channel) => {
          if (cancelled) return
          setMap((prev) => {
            if (prev.get(slug)?.id === channel.id) return prev
            const next = new Map(prev)
            next.set(slug, channel)
            return next
          })
        })
        .catch(() => {
          // A tab whose channel 404s just shows its slug; no banner needed.
        })
    }

    return () => {
      cancelled = true
    }
  }, [key])

  return map
}

export function useVods(slug: string | null): AsyncState<Vod[]> {
  return useCachedResource(
    slug ? `vods:${slug}` : null,
    120_000,
    (_key, signal) => fetchVods(slug as string, signal),
    'Could not load VODs',
  )
}

/** Warms the VOD cache for a tab so switching to it feels instant. */
export function prefetchVods(slug: string): void {
  kickCache.fetch(`vods:${slug}`, 120_000, (signal) => fetchVods(slug, signal)).catch(() => {})
}

/** Explicit refresh bypasses the TTL while updating the shared player/browse cache. */
export async function refreshVods(slug: string, signal?: AbortSignal): Promise<Vod[]> {
  const vods = await fetchVods(slug, signal)
  kickCache.set(`vods:${slug}`, vods, 120_000)
  return vods
}
